import paper from 'paper';
import * as NeuralaMath from '../../../utils/math';

export interface GridState {
    columns: number;
    rows: number;
    color: string;
    visible: boolean;
}

export const MIN_SIZE = 1;
export const MAX_SIZE = 100;

export class GridLayer extends paper.Layer {
    private neuralaColumns = 10;
    private neuralaRows = 10;
    private neuralaColor = new paper.Color('rgb(238, 168, 59)');

    constructor(options: Object) {
        super(options);
        this.visible = false;
    }

    public getColumns(): number {
        return this.neuralaColumns;
    }

    public getRows(): number {
        return this.neuralaRows;
    }

    public getColor(): string {
        return this.neuralaColor.toCSS(true);
    }

    public setColor(color: string): void {
        this.neuralaColor = new paper.Color(color);
    }

    public drawLayer(columns: number, rows: number): void {
        this.neuralaColumns = NeuralaMath.adjustToMinMax(columns, MIN_SIZE, MAX_SIZE);
        this.neuralaRows = NeuralaMath.adjustToMinMax(rows, MIN_SIZE, MAX_SIZE);
        this.redrawLayer();
    }

    public adjustPointToGrid(point: paper.Point): paper.Point {
        // when you add 0.1 and 0.2 you never get exactly 0.3
        // therefore we must use floor and normalize point
        // For more info about numbers  with floating point see https://docs.python.org/2/tutorial/floatingpoint.html
        // This is applicable for JS and any other language
        // because it is specified in standard https://en.wikipedia.org/wiki/IEEE_754
        const assetHeight = this.project.layers['assetLayer'].bounds.height;
        const assetWidth = this.project.layers['assetLayer'].bounds.width;
        const boundsPoint = this.project.layers['assetLayer'].bounds.point;

        const zoom = this.view.zoom;
        const radiusX = 5 / zoom;
        const radiusY = 5 / zoom;

        const vStep = NeuralaMath.floorWithPrecision(assetHeight / this.neuralaRows);
        const y = NeuralaMath.snap(point.y - boundsPoint.y, vStep, radiusY);

        const hStep = NeuralaMath.floorWithPrecision(assetWidth / this.neuralaColumns);
        const x = NeuralaMath.snap(point.x - boundsPoint.x, hStep, radiusX);

        return new paper.Point(boundsPoint.x + x, boundsPoint.y + y);
    }

    public redrawLayer(): void {
        this.removeChildren();
        this.drawColumns();
        this.drawRows();
    }

    private drawColumns() {
        if (this.neuralaColumns < 2) {
            return;
        }

        const assetWidth = this.project.layers['assetLayer'].bounds.width;
        const step = assetWidth / this.neuralaColumns;
        const minX = this.project.layers['assetLayer'].bounds.topLeft.x;
        const yTop = this.project.layers['assetLayer'].bounds.topLeft.y;
        const yBottom = this.project.layers['assetLayer'].bounds.bottomLeft.y;

        for (let i = 1; i < this.neuralaColumns; i++) {
            const x = minX + i * step;
            const line = new paper.Path.Line({
                from: new paper.Point(x, yTop),
                to: new paper.Point(x, yBottom),
                fillColor: this.neuralaColor,
                strokeColor: this.neuralaColor,
                strokeScaling: false
            });
            this.addChild(line);
        }
    }

    private drawRows() {
        if (this.neuralaRows < 2) {
            return;
        }

        const assetHeight = this.project.layers['assetLayer'].bounds.height;
        const step = assetHeight / this.neuralaRows;
        const minY = this.project.layers['assetLayer'].bounds.topLeft.y;
        const xLeft = this.project.layers['assetLayer'].bounds.topLeft.x;
        const xRight = this.project.layers['assetLayer'].bounds.topRight.x;

        for (let i = 1; i < this.neuralaRows; i++) {
            const y = minY + i * step;
            const line = new paper.Path.Line({
                from: new paper.Point(xLeft, y),
                to: new paper.Point(xRight, y),
                fillColor: this.neuralaColor,
                strokeColor: this.neuralaColor,
                strokeScaling: false
            });
            this.addChild(line);
        }
    }

    public hide(): void {
        this.visible = false;
    }

    public show(): void {
        this.visible = true;
    }
}



// WEBPACK FOOTER //
// ./src/components/workspace/objects/grid.ts